import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shirt,
  ArrowLeft,
  Clock,
  Building2,
  Home,
  User,
  Calendar,
  CheckCircle,
  Package,
  TruckIcon,
  Filter,
  Printer,
  Trash2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DetalleSolicitudModal from '@/components/solicitudes/DetalleSolicitudModal';
import AlistarLenceriaModal from '@/components/lenceria/AlistarLenceriaModal';
import CrearEntregaDirectaModal from '@/components/lenceria/CrearEntregaDirectaModal';
import VerComprobanteModal from '@/components/conductor/VerComprobanteModal';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import ExportExcelButton from '@/components/ExportExcelButton';
import {
  fetchSolicitudesPendientes,
  fetchEntregas,
  exportSolicitudesPendientes,
  exportEntregas,
  cancelarEntrega,
} from '@/services/entregasLenceria.service';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';

const ITEMS_PER_PAGE = 10;

const RecepcionRopaPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  // Estado de tabs
  const [activeTab, setActiveTab] = useState('pendientes');

  // Estados para filtros
  // Obtener fecha local sin conversión a UTC
  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const today = getLocalDateString(new Date());
  const getTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return getLocalDateString(tomorrow);
  };
  const [filters, setFilters] = useState({});
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Datos por tab
  const [solicitudesPendientes, setSolicitudesPendientes] = useState([]);
  const [entregasAlistadas, setEntregasAlistadas] = useState([]);
  const [entregasEntregadas, setEntregasEntregadas] = useState([]);
  const [entregasRecoleccion, setEntregasRecoleccion] = useState([]);

  // Loading por tab
  const [isLoadingPendientes, setIsLoadingPendientes] = useState(false);
  const [isLoadingAlistadas, setIsLoadingAlistadas] = useState(false);
  const [isLoadingEntregadas, setIsLoadingEntregadas] = useState(false);
  const [isLoadingRecoleccion, setIsLoadingRecoleccion] = useState(false);

  // Paginación por tab
  const [currentPagePendientes, setCurrentPagePendientes] = useState(1);
  const [currentPageAlistadas, setCurrentPageAlistadas] = useState(1);
  const [currentPageEntregadas, setCurrentPageEntregadas] = useState(1);
  const [currentPageRecoleccion, setCurrentPageRecoleccion] = useState(1);

  const [totalPendientes, setTotalPendientes] = useState(0);
  const [totalAlistadas, setTotalAlistadas] = useState(0);
  const [totalEntregadas, setTotalEntregadas] = useState(0);
  const [totalRecoleccion, setTotalRecoleccion] = useState(0);

  // Modal de detalle
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [isDetalleModalOpen, setIsDetalleModalOpen] = useState(false);

  // Modal de alistamiento
  const [solicitudAlistar, setSolicitudAlistar] = useState(null);
  const [isAlistarModalOpen, setIsAlistarModalOpen] = useState(false);

  // Modal de comprobante
  const [isComprobanteModalOpen, setIsComprobanteModalOpen] = useState(false);
  const [solicitudComprobante, setSolicitudComprobante] = useState(null);

  // Modal de entrega directa
  const [isEntregaDirectaModalOpen, setIsEntregaDirectaModalOpen] = useState(false);

  // Modal de cancelación de alistamiento
  const [entregaACancelar, setEntregaACancelar] = useState(null);
  const [motivoCancelacion, setMotivoCancelacion] = useState('');
  const [isCancelingEntrega, setIsCancelingEntrega] = useState(false);

  // Estado de impresión
  const [isPrinting, setIsPrinting] = useState(false);

  // Handlers
  const handleVerDetalle = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setIsDetalleModalOpen(true);
  };

  const handleAlistar = (solicitud) => {
    setSolicitudAlistar(solicitud);
    setIsAlistarModalOpen(true);
  };

  const handleAlistamientoSuccess = () => {
    loadPendientes();
    loadAlistadas();
    toast({
      title: 'Éxito',
      description: 'Lencería alistada correctamente',
    });
  };

  const handleEntregaDirectaSuccess = () => {
    loadAlistadas();
    loadEntregadas();
  };

  const handleVerComprobante = (entrega) => {
    const solicitudFormateada = {
      ...entrega,
      tipo: 'lenceria',
      consecutivo: entrega.consecutivo_entrega || entrega.consecutivo,
      displayName: entrega.hospedaje?.nombre || entrega.nombrePropiedad || 'Sin hospedaje',
      solicitante: entrega.solicitante?.nombre || 'Desconocido',
      fechaSolicitud: entrega.createdAt,
      fechaEntrega: entrega.fecha_entrega,
      items: entrega.items || [],
      edificio: entrega.edificio,
      hospedaje: entrega.hospedaje,
      entregadoPor: entrega.conductor || entrega.entregadoPor,
      receptor_nombre: entrega.receptor_nombre,
      firma: entrega.firma,
      firmaDigital: entrega.firmaDigital,
      observaciones: entrega.observaciones
    };
    setSolicitudComprobante(solicitudFormateada);
    setIsComprobanteModalOpen(true);
  };
  
  // Manejar impresión Bluetooth
  const handlePrintBluetooth = async (entrega) => {
    setIsPrinting(true);
    try {
      const printer = getPrinterInstance();
      
      // Formatear la entrega para impresión
      const entregaFormateada = {
        ...entrega,
        tipo: 'lenceria',
        consecutivo: entrega.consecutivo_entrega || entrega.consecutivo || entrega._id?.slice(-6).toUpperCase(),
        fechaEntrega: entrega.fecha_entrega || entrega.createdAt,
        solicitante: entrega.creator?.nombre || entrega.solicitante?.nombre || 'N/A',
        entregadoPor: {
          nombre: entrega.conductor?.nombre || entrega.entregadoPor?.nombre || 'Conductor'
        },
        items: entrega.items?.map(item => ({
          nombre: item.nombre || item.tipo_prenda?.nombre || 'Item',
          cantidad: item.cantidad || 0,
          peso: item.peso || item.peso_unitario || 0,
          descripcion: item.descripcion || '',
          observaciones: item.observaciones || item.observacion || ''
        })) || []
      };
      
      await printer.printEntregaReceipt(entregaFormateada);
      
      toast({
        title: 'Éxito',
        description: 'Comprobante impreso correctamente',
      });
    } catch (error) {
      console.error('Error al imprimir:', error);
      toast({
        title: 'Error al imprimir',
        description: error.message || 'No se pudo conectar con la impresora Bluetooth',
        variant: 'destructive',
      });
    } finally {
      setIsPrinting(false);
    }
  };

  // Definir campos de filtro para FilterDrawer
  const filterFields = [
    {
      id: 'fechaEntregaInicio',
      label: 'Fecha Inicio',
      type: 'date',
      placeholder: 'Fecha desde'
    },
    {
      id: 'fechaEntregaFin',
      label: 'Fecha Fin',
      type: 'date',
      placeholder: 'Fecha hasta'
    }
  ];

  // Obtener rango de fecha seleccionada
  const getDateRange = useCallback(() => {
    let inicioDelDia, finDelDia;

    if (filters.fechaEntregaInicio && filters.fechaEntregaFin) {
      // Rango personalizado - Crear fechas en zona horaria local
      const [yearInicio, mesInicio, diaInicio] = filters.fechaEntregaInicio.split('-').map(Number);
      const [yearFin, mesFin, diaFin] = filters.fechaEntregaFin.split('-').map(Number);
      
      inicioDelDia = new Date(yearInicio, mesInicio - 1, diaInicio, 0, 0, 0, 0);
      finDelDia = new Date(yearFin, mesFin - 1, diaFin, 23, 59, 59, 999);
    } else if (filters.fechaEntregaInicio) {
      // Solo fecha inicio
      const [year, mes, dia] = filters.fechaEntregaInicio.split('-').map(Number);
      inicioDelDia = new Date(year, mes - 1, dia, 0, 0, 0, 0);
      finDelDia = new Date(year, mes - 1, dia, 23, 59, 59, 999);
    } else if (filters.fechaEntregaFin) {
      // Solo fecha fin
      const [year, mes, dia] = filters.fechaEntregaFin.split('-').map(Number);
      inicioDelDia = new Date(year, mes - 1, dia, 0, 0, 0, 0);
      finDelDia = new Date(year, mes - 1, dia, 23, 59, 59, 999);
    } else {
      // Sin filtros, usar hoy por defecto
      const [year, mes, dia] = today.split('-').map(Number);
      inicioDelDia = new Date(year, mes - 1, dia, 0, 0, 0, 0);
      finDelDia = new Date(year, mes - 1, dia, 23, 59, 59, 999);
    }

    return { inicioDelDia, finDelDia };
  }, [filters.fechaEntregaInicio, filters.fechaEntregaFin, today]);

  const getDisplayedDateLabel = () => {
    if (filters.fechaEntregaInicio && filters.fechaEntregaFin) {
      const inicio = new Date(filters.fechaEntregaInicio + 'T00:00:00');
      const fin = new Date(filters.fechaEntregaFin + 'T00:00:00');
      return `${inicio.toLocaleDateString('es-ES')} - ${fin.toLocaleDateString('es-ES')}`;
    } else if (filters.fechaEntregaInicio) {
      const fecha = new Date(filters.fechaEntregaInicio + 'T00:00:00');
      return fecha.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else if (filters.fechaEntregaFin) {
      const fecha = new Date(filters.fechaEntregaFin + 'T00:00:00');
      return fecha.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else {
      const baseDate = new Date(today + 'T00:00:00');
      return baseDate.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  // Manejar aplicación de filtros
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setIsFilterDrawerOpen(false);
  };

  // Manejar eliminación de filtro individual
  const handleRemoveFilter = (filterId) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[filterId];
      return newFilters;
    });
  };

  // Filtros rápidos - Integrados con FilterDrawer
  const handleQuickFilterToday = () => {
    setFilters(prev => ({
      ...prev,
      fechaEntregaInicio: today,
      fechaEntregaFin: today
    }));
  };

  const handleQuickFilterTomorrow = () => {
    const tomorrow = getTomorrow();
    setFilters(prev => ({
      ...prev,
      fechaEntregaInicio: tomorrow,
      fechaEntregaFin: tomorrow
    }));
  };

  const isDateSelected = (date) => {
    return filters.fechaEntregaInicio === date && filters.fechaEntregaFin === date;
  };

  // Contar filtros activos
  const getActiveFiltersCount = () => {
    let count = 0;
    // Contar todos los filtros aplicados
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        count++;
      }
    });
    return count;
  };

  // Columnas comunes
  const baseColumns = [
    {
      id: 'consecutivo',
      label: 'Consecutivo',
      render: (row) => (
        <span className="font-mono text-sm font-medium">
          {row.consecutivo_entrega || row.consecutivo_recoleccion || 'N/A'}
        </span>
      ),
    },
    {
      id: 'fecha',
      label: 'Fecha creación',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })}
          </span>
        </div>
      ),
    },
    {
      id: 'edificio',
      label: 'Edificio',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-blue-600" />
          <div>
            <div className="font-medium">{row.edificio?.nombre}</div>
            <div className="text-xs text-gray-500">{row.edificio?.ciudad}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'hospedaje',
      label: 'Activo',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-green-600" />
          <div>
            <div className="font-medium">{row.hospedaje?.nombre || row.nombrePropiedad}</div>
            <div className="text-xs text-gray-500 capitalize">{row.hospedaje?.tipo}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'solicitante',
      label: 'Solicitante',
      render: (row) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-purple-600" />
          <span className="text-sm">{row.creator?.nombre}</span>
        </div>
      ),
    },
    {
      id: 'total_solicitado',
      label: 'Total Prendas',
      render: (row) => (
        <Badge variant="outline" className="font-semibold">
          {row.total_solicitado} prendas
        </Badge>
      ),
    },
  ];

  const columnsPendientes = [
    {
      id: 'fecha',
      label: 'Fecha creación',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })}
          </span>
        </div>
      ),
    },
    {
      id: 'fechaEntrega',
      label: 'Fecha Entrega',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm">
            {new Date(row.fechaEntrega).toLocaleString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </span>
        </div>
      ),
    },
    {
      id: 'edificio',
      label: 'Edificio',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-blue-600" />
          <div>
            <div className="font-medium">{row.edificio?.nombre}</div>
            <div className="text-xs text-gray-500">{row.edificio?.ciudad}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'hospedaje',
      label: 'Activo',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-green-600" />
          <div>
            <div className="font-medium">{row.hospedaje?.nombre || row.nombrePropiedad}</div>
            <div className="text-xs text-gray-500 capitalize">{row.hospedaje?.tipo}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'solicitante',
      label: 'Solicitante',
      render: (row) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-purple-600" />
          <span className="text-sm">{row.creator?.nombre}</span>
        </div>
      ),
    },
    {
      id: 'total_solicitado',
      label: 'Total Prendas',
      render: (row) => (
        <Badge variant="outline" className="font-semibold">
          {row.total_solicitado} prendas
        </Badge>
      ),
    },
    {
      id:'peso_total',
      label:'Peso Total',
      render:(row)=>(
        <Badge variant="outline" className="font-semibold">
          {row.peso_total/1000} kg
        </Badge>
      )
    },

    {
      id: 'estado',
      label: 'Estado',
      render: (row) => (
        <Badge className="bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          {row.estado || 'Pendiente'}
        </Badge>
      ),
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-detalle',
          icon: 'Eye',
          tooltip: 'Ver Detalle',
          className: 'text-blue-600 hover:bg-blue-50',
        },
        {
          key: 'alistar',
          icon: 'Package',
          tooltip: 'Alistar',
          className: 'text-green-600 hover:bg-green-50',
        },
      ],
    },
  ];

  const columnsAlistadas = [
    {
      id: 'consecutivo',
      label: 'Consecutivo',
      render: (row) => (
        <span className="font-mono text-sm font-medium">
          {row.consecutivo_entrega || 'N/A'}
        </span>
      ),
    },
    {
      id: 'fecha',
      label: 'Fecha alistamiento',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })}
          </span>
        </div>
      ),
    },
    {
      id: 'edificio',
      label: 'Edificio',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-blue-600" />
          <div>
            <div className="font-medium">{row.edificio?.nombre}</div>
            <div className="text-xs text-gray-500">{row.edificio?.ciudad}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'hospedaje',
      label: 'Activo',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-green-600" />
          <div>
            <div className="font-medium">{row.hospedaje?.nombre || row.nombrePropiedad}</div>
            <div className="text-xs text-gray-500 capitalize">{row.hospedaje?.tipo}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'solicitante',
      label: 'Solicitante',
      render: (row) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-purple-600" />
          <span className="text-sm">{row.solicitante?.nombre}</span>
        </div>
      ),
    },
    {
      id: 'total_solicitado',
      label: 'Total Prendas',
      render: (row) => (
        <Badge variant="outline" className="font-semibold">
          {row.total_solicitado} prendas
        </Badge>
      ),
    },
    {
      id:'peso_total',
      label:'Peso Total',
      render:(row)=>(
        <Badge variant="outline" className="font-semibold">
          {row.peso_total/1000} kg
        </Badge>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      render: () => (
        <Badge className="bg-blue-100 text-blue-800">
          <Clock className="h-3 w-3 mr-1" />
          Alistado
        </Badge>
      ),
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-detalle',
          icon: 'Eye',
          tooltip: 'Ver Detalle',
          className: 'text-blue-600 hover:bg-blue-50',
        },
        {
          key: 'imprimir-bluetooth',
          icon: 'Printer',
          tooltip: 'Imprimir en Bluetooth',
          className: 'text-purple-600 hover:bg-purple-50',
        },
        {
          key: 'cancelar-alistamiento',
          icon: 'Trash',
          tooltip: 'Cancelar alistamiento (no afecta inventario)',
          className: 'text-red-600 hover:bg-red-50',
        },
      ],
    },
  ];

  const columnsEntregadas = [
    ...baseColumns,
    {
      id: 'fechaEntrega',
      label: 'Fecha Entrega',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm">
            {(() => {
              const fecha =
                row?.fechaEntrega ||
                row?.fecha_entrega ||
                null;

              return fecha
                ? new Date(fecha).toLocaleDateString('es-ES')
                : 'No aplica';
            })()}
          </span>
        </div>
      ),
    },
    {
      id: 'estado',
      label: 'Estado',
      render: () => (
        <Badge className="bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Entregado
        </Badge>
      ),
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-comprobante',
          icon: 'FileText',
          tooltip: 'Ver Comprobante',
          className: 'text-green-600 hover:bg-green-50',
        },
        {
          key: 'imprimir-bluetooth',
          icon: 'Printer',
          tooltip: 'Imprimir en Bluetooth',
          className: 'text-blue-600 hover:bg-blue-50',
        },
      ],
    },
  ];

  const columnsRecoleccion = [
    ...baseColumns,
    {
      id: 'fechaEntrega',
      label: 'Fecha Recolección',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm">
            {(() => {
              const fecha =
                row?.fechaEntrega ||
                row?.fecha_entrega ||
                null;

              return fecha
                ? new Date(fecha).toLocaleDateString('es-ES')
                : 'No aplica';
            })()}
          </span>
        </div>
      ),
    },
    {
      id: 'peso_total',
      label: 'Peso Total',
      render: (row) => {
        const pesoTotal = row.items?.reduce((sum, item) => sum + ((item.cantidad || 0) * (item.peso_unitario || 0)), 0) || 0;
        return (
          <span className="text-sm text-gray-600">
            {pesoTotal > 0 ? `${(pesoTotal / 1000).toFixed(2)} kg` : '-'}
          </span>
        );
      },
    },
    {
      id: 'estado',
      label: 'Estado',
      render: () => (
        <Badge className="bg-orange-100 text-orange-800">
          <TruckIcon className="h-3 w-3 mr-1" />
          Recolección
        </Badge>
      ),
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-comprobante',
          icon: 'FileText',
          tooltip: 'Ver Comprobante',
          className: 'text-orange-600 hover:bg-orange-50',
        },
        {
          key: 'imprimir-bluetooth',
          icon: 'Printer',
          tooltip: 'Imprimir en Bluetooth',
          className: 'text-blue-600 hover:bg-blue-50',
        },
      ],
    },
  ];

  const handleAction = (actionKey, row) => {
    if (actionKey === 'ver-detalle') {
      handleVerDetalle(row);
    } else if (actionKey === 'alistar') {
      handleAlistar(row);
    } else if (actionKey === 'ver-comprobante') {
      handleVerComprobante(row);
    } else if (actionKey === 'imprimir-bluetooth') {
      handlePrintBluetooth(row);
    } else if (actionKey === 'cancelar-alistamiento') {
      // Solo se permite cancelar si la entrega está en estado 'alistado'
      if (row.estado && row.estado !== 'alistado') {
        toast({
          title: 'No se puede cancelar',
          description: `Solo se pueden eliminar entregas en estado "alistado". Esta entrega está "${row.estado}".`,
          variant: 'destructive',
        });
        return;
      }
      setEntregaACancelar(row);
      setMotivoCancelacion('');
    }
  };

  const handleConfirmarCancelacion = async () => {
    if (!entregaACancelar) return;
    if (!motivoCancelacion.trim()) {
      toast({
        title: 'Motivo requerido',
        description: 'Debes indicar el motivo de la cancelación.',
        variant: 'destructive',
      });
      return;
    }

    setIsCancelingEntrega(true);
    try {
      const usuarioPayload = {
        _id: user?._id,
        id: user?._id,
        nombre: user?.name || user?.nombre,
      };

      await cancelarEntrega(entregaACancelar._id, usuarioPayload, motivoCancelacion.trim());

      toast({
        title: 'Alistamiento eliminado',
        description: `Se canceló el alistamiento de "${entregaACancelar.hospedaje?.nombre || entregaACancelar.nombrePropiedad || 'la entrega'}". El inventario no se vio afectado porque el alistamiento no descuenta stock.`,
      });

      setEntregaACancelar(null);
      setMotivoCancelacion('');
      // Refrescar la lista de alistadas y pendientes (porque el stock se devolvió)
      loadAlistadas();
      loadPendientes();
    } catch (error) {
      console.error('Error al cancelar alistamiento:', error);
      toast({
        title: 'No se pudo eliminar el alistamiento',
        description: error?.message || 'Intenta de nuevo en unos segundos.',
        variant: 'destructive',
      });
    } finally {
      setIsCancelingEntrega(false);
    }
  };

  // Loaders
  const loadPendientes = useCallback(async () => {
    setIsLoadingPendientes(true);
    try {
      const { inicioDelDia, finDelDia } = getDateRange();

      const response = await fetchSolicitudesPendientes({
        page: currentPagePendientes,
        limit: ITEMS_PER_PAGE,
        fechaEntregaInicio: inicioDelDia.toISOString(),
        fechaEntregaFin: finDelDia.toISOString()
      });

      if (response) {
        setSolicitudesPendientes(response.solicitudes || []);
        setTotalPendientes(response.total || 0);
      } else {
        setSolicitudesPendientes([]);
        setTotalPendientes(0);
      }
    } catch (error) {
      console.error('Error al cargar solicitudes pendientes:', error);
      setSolicitudesPendientes([]);
      setTotalPendientes(0);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las solicitudes pendientes',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingPendientes(false);
    }
  }, [currentPagePendientes, getDateRange, toast]);

  const loadAlistadas = useCallback(async () => {
    setIsLoadingAlistadas(true);
    try {
      // NOTA: las alistadas son una cola de trabajo: se muestran TODAS las que
      // aún están en estado 'alistado' sin importar cuándo se alistaron, para
      // que las de días anteriores no se queden sin entregar.
      const response = await fetchEntregas({
        page: currentPageAlistadas,
        limit: ITEMS_PER_PAGE,
        estado: 'alistado',
      });

      if (response) {
        setEntregasAlistadas(response.entregas || []);
        setTotalAlistadas(response.total || 0);
      } else {
        setEntregasAlistadas([]);
        setTotalAlistadas(0);
      }
    } catch (error) {
      console.error('Error al cargar entregas alistadas:', error);
      setEntregasAlistadas([]);
      setTotalAlistadas(0);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas alistadas',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingAlistadas(false);
    }
  }, [currentPageAlistadas, toast]);

  const loadEntregadas = useCallback(async () => {
    setIsLoadingEntregadas(true);
    try {
      const { inicioDelDia, finDelDia } = getDateRange();

      const response = await fetchEntregas({
        page: currentPageEntregadas,
        limit: ITEMS_PER_PAGE,
        estado: 'entregado',
        fecha_entrega_desde: inicioDelDia.toISOString(),
        fecha_entrega_hasta: finDelDia.toISOString()
      });

      if (response) {
        setEntregasEntregadas(response.entregas || []);
        setTotalEntregadas(response.total || 0);
      } else {
        setEntregasEntregadas([]);
        setTotalEntregadas(0);
      }
    } catch (error) {
      console.error('Error al cargar entregas entregadas:', error);
      setEntregasEntregadas([]);
      setTotalEntregadas(0);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas entregadas',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingEntregadas(false);
    }
  }, [currentPageEntregadas, getDateRange, toast]);

  const loadRecoleccion = useCallback(async () => {
    setIsLoadingRecoleccion(true);
    try {
      const { inicioDelDia, finDelDia } = getDateRange();

      const response = await fetchEntregas({
        page: currentPageRecoleccion,
        limit: ITEMS_PER_PAGE,
        estado: 'recoleccion',
        fecha_entrega_desde: inicioDelDia.toISOString(),
        fecha_entrega_hasta: finDelDia.toISOString()
      });

      if (response) {
        setEntregasRecoleccion(response.entregas || []);
        setTotalRecoleccion(response.total || 0);
      } else {
        setEntregasRecoleccion([]);
        setTotalRecoleccion(0);
      }
    } catch (error) {
      console.error('Error al cargar entregas de recolección:', error);
      setEntregasRecoleccion([]);
      setTotalRecoleccion(0);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas de recolección',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingRecoleccion(false);
    }
  }, [currentPageRecoleccion, getDateRange, toast]);

  // Cargar datos según el tab activo y filtros
  useEffect(() => {
    if (activeTab === 'pendientes') {
      loadPendientes();
    } else if (activeTab === 'alistadas') {
      loadAlistadas();
    } else if (activeTab === 'entregadas') {
      loadEntregadas();
    } else if (activeTab === 'recoleccion') {
      loadRecoleccion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    filters.fechaEntregaInicio,
    filters.fechaEntregaFin,
    currentPagePendientes,
    currentPageAlistadas,
    currentPageEntregadas,
    currentPageRecoleccion
  ]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className=""
    >
      {/* Header */}
      <Card className="card-gradient-bg shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/lavanderia')}
                className="h-10 w-10"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="p-3 bg-blue-100 rounded-full">
                <Shirt className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  Lencería horizontal
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  {getDisplayedDateLabel()}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Filtros Rápidos - Solo para Pendientes */}
              {activeTab === 'pendientes' && (
                <div className="flex items-center gap-2">
                  <Button
                    variant={isDateSelected(today) ? "default" : "outline"}
                    size="sm"
                    onClick={handleQuickFilterToday}
                    className="flex items-center gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Hoy
                  </Button>
                  <Button
                    variant={isDateSelected(getTomorrow()) ? "default" : "outline"}
                    size="sm"
                    onClick={handleQuickFilterTomorrow}
                    className="flex items-center gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Mañana
                  </Button>
                </div>
              )}
              <Button
                variant="outline"
                onClick={() => setIsFilterDrawerOpen(true)}
                className="flex items-center gap-2"
              >
                <Filter className="h-4 w-4" />
                Filtros
                {getActiveFiltersCount() > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {getActiveFiltersCount()}
                  </Badge>
                )}
              </Button>
              
              {/* Botón de Exportar a Excel */}
              {activeTab === 'pendientes' && (
                <ExportExcelButton
                  service={async (filterParams) => {
                    const { inicioDelDia, finDelDia } = getDateRange();
                    // Usar el endpoint específico de exportación
                    const result = await exportSolicitudesPendientes({
                      ...filterParams,
                      fechaEntregaInicio: inicioDelDia.toISOString(),
                      fechaEntregaFin: finDelDia.toISOString()
                    });
                    // El servicio ya retorna el array directamente
                    return Array.isArray(result) ? result : [];
                  }}
                  filters={{
                    limit: 10000
                  }}
                  columns={[
                    { header: 'Solicitud ID', key: 'solicitud_id' },
                    { header: 'Edificio', key: 'edificio.nombre' },
                    { header: 'Ciudad', key: 'edificio.ciudad' },
                    { header: 'Hospedaje', key: 'hospedaje.nombre' },
                    { header: 'Solicitante', key: 'solicitante.nombre' },
                    { header: 'Total Items', key: 'total_items' },
                    { header: 'Peso Total (kg)', key: 'peso_total' },
                    { header: 'Estado', key: 'estado' },
                    { header: 'Fecha Creación', key: 'createdAt' },
                  ]}
                  fileName={`lenceria-pendientes-${new Date().toISOString().split('T')[0]}`}
                  dataMapper={(item) => ({
                    ...item,
                    peso_total: item.peso_total ? (item.peso_total / 1000).toFixed(2) : '0',
                  })}
                />
              )}
              {activeTab === 'alistadas' && (
                <ExportExcelButton
                  service={async (filterParams) => {
                    // Mismo criterio que la lista: TODAS las alistadas sin filtrar por fecha
                    const result = await exportEntregas({
                      estado: 'alistado',
                    });
                    // El servicio ya retorna el array directamente
                    return Array.isArray(result) ? result : [];
                  }}
                  filters={{
                    limit: 10000
                  }}
                  columns={[
                    { header: 'Solicitud ID', key: 'solicitud_id' },
                    { header: 'Edificio', key: 'edificio.nombre' },
                    { header: 'Ciudad', key: 'edificio.ciudad' },
                    { header: 'Hospedaje', key: 'hospedaje.nombre' },
                    { header: 'Solicitante', key: 'solicitante.nombre' },
                    { header: 'Alistador', key: 'alistador.nombre' },
                    { header: 'Total Items', key: 'total_items' },
                    { header: 'Peso Total (kg)', key: 'peso_total' },
                    { header: 'Estado', key: 'estado' },
                    { header: 'Fecha Alistamiento', key: 'fecha_alistamiento' },
                  ]}
                  fileName={`lenceria-alistadas-${new Date().toISOString().split('T')[0]}`}
                  dataMapper={(item) => ({
                    ...item,
                    peso_total: item.peso_total ? (item.peso_total / 1000).toFixed(2) : '0',
                  })}
                />
              )}
              {activeTab === 'entregadas' && (
                <ExportExcelButton
                  service={async (filterParams) => {
                    const { inicioDelDia, finDelDia } = getDateRange();
                    // Usar el endpoint específico de exportación
                    const result = await exportEntregas({
                      estado: 'entregado',
                      fecha_entrega_desde: inicioDelDia.toISOString(),
                      fecha_entrega_hasta: finDelDia.toISOString()
                    });
                    // El servicio ya retorna el array directamente
                    return Array.isArray(result) ? result : [];
                  }}
                  filters={{
                    limit: 10000
                  }}
                  columns={[
                    { header: 'Solicitud ID', key: 'solicitud_id' },
                    { header: 'Consecutivo Entrega', key: 'consecutivo_entrega' },
                    { header: 'Edificio', key: 'edificio.nombre' },
                    { header: 'Ciudad', key: 'edificio.ciudad' },
                    { header: 'Hospedaje', key: 'hospedaje.nombre' },
                    { header: 'Solicitante', key: 'solicitante.nombre' },
                    { header: 'Conductor', key: 'conductor.nombre' },
                    { header: 'Receptor', key: 'receptor_nombre' },
                    { header: 'Total Items', key: 'total_items' },
                    { header: 'Peso Total (kg)', key: 'peso_total' },
                    { header: 'Estado', key: 'estado' },
                    { header: 'Fecha Entrega', key: 'fecha_entrega' },
                  ]}
                  fileName={`lenceria-entregadas-${new Date().toISOString().split('T')[0]}`}
                  dataMapper={(item) => ({
                    ...item,
                    peso_total: item.peso_total ? (item.peso_total / 1000).toFixed(2) : '0',
                  })}
                />
              )}
              {activeTab === 'recoleccion' && (
                <ExportExcelButton
                  service={async (filterParams) => {
                    const { inicioDelDia, finDelDia } = getDateRange();
                    // Usar el endpoint específico de exportación
                    const result = await exportEntregas({
                      estado: 'recoleccion',
                      fecha_entrega_desde: inicioDelDia.toISOString(),
                      fecha_entrega_hasta: finDelDia.toISOString()
                    });
                    // El servicio ya retorna el array directamente
                    return Array.isArray(result) ? result : [];
                  }}
                  filters={{
                    limit: 10000
                  }}
                  columns={[
                    { header: 'Solicitud ID', key: 'solicitud_id' },
                    { header: 'Consecutivo Recolección', key: 'consecutivo_recoleccion' },
                    { header: 'Edificio', key: 'edificio.nombre' },
                    { header: 'Ciudad', key: 'edificio.ciudad' },
                    { header: 'Hospedaje', key: 'hospedaje.nombre' },
                    { header: 'Solicitante', key: 'solicitante.nombre' },
                    { header: 'Conductor', key: 'conductor.nombre' },
                    { header: 'Receptor', key: 'receptor_nombre' },
                    { header: 'Total Items', key: 'total_items' },
                    { header: 'Peso Total (kg)', key: 'peso_total' },
                    { header: 'Estado', key: 'estado' },
                    { header: 'Fecha Recolección', key: 'fecha_entrega' },
                  ]}
                  fileName={`lenceria-recoleccion-${new Date().toISOString().split('T')[0]}`}
                  dataMapper={(item) => ({
                    ...item,
                    peso_total: item.peso_total ? (item.peso_total / 1000).toFixed(2) : '0',
                  })}
                />
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Filtros Aplicados */}
      {getActiveFiltersCount() > 0 && (
        <AppliedFilters
          filters={filters}
          fields={filterFields}
          onRemoveFilter={handleRemoveFilter}
        />
      )}

      {/* Tabs y tablas */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6 bg-gray-100 p-1 rounded-lg h-auto gap-1">
              <TabsTrigger 
                value="pendientes" 
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-yellow-700 data-[state=active]:font-semibold data-[state=active]:border-yellow-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <Clock className="h-4 w-4" />
                <span className="hidden sm:inline">Solicitudes</span>
                {totalPendientes > 0 && (
                  <Badge variant="secondary" className="ml-1 data-[state=active]:bg-yellow-100 data-[state=active]:text-yellow-700">
                    {totalPendientes}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="alistadas" 
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-700 data-[state=active]:font-semibold data-[state=active]:border-blue-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <Shirt className="h-4 w-4" />
                <span className="hidden sm:inline">Alistadas</span>
                {totalAlistadas > 0 && (
                  <Badge variant="secondary" className="ml-1 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
                    {totalAlistadas}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="recoleccion" 
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-orange-700 data-[state=active]:font-semibold data-[state=active]:border-orange-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <TruckIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Recolección</span>
                {totalRecoleccion > 0 && (
                  <Badge variant="secondary" className="ml-1 data-[state=active]:bg-orange-100 data-[state=active]:text-orange-700">
                    {totalRecoleccion}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="entregadas" 
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-green-700 data-[state=active]:font-semibold data-[state=active]:border-green-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <CheckCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Entregadas</span>
                {totalEntregadas > 0 && (
                  <Badge variant="secondary" className="ml-1 data-[state=active]:bg-green-100 data-[state=active]:text-green-700">
                    {totalEntregadas}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Tab Pendientes */}
            <TabsContent value="pendientes" className="space-y-4">
              <DataTable
                columns={columnsPendientes}
                data={solicitudesPendientes}
                isLoading={isLoadingPendientes}
                onAction={handleAction}
              />
              <Pagination
                currentPage={currentPagePendientes}
                totalItems={totalPendientes}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPagePendientes}
              />
            </TabsContent>

            {/* Tab Alistadas */}
            <TabsContent value="alistadas" className="space-y-4">
              <DataTable
                columns={columnsAlistadas}
                data={entregasAlistadas}
                isLoading={isLoadingAlistadas}
                onAction={handleAction}
              />
              <Pagination
                currentPage={currentPageAlistadas}
                totalItems={totalAlistadas}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPageAlistadas}
              />
            </TabsContent>

            {/* Tab Recolección */}
            <TabsContent value="recoleccion" className="space-y-4">
              <DataTable
                columns={columnsRecoleccion}
                data={entregasRecoleccion}
                isLoading={isLoadingRecoleccion}
                onAction={handleAction}
              />
              <Pagination
                currentPage={currentPageRecoleccion}
                totalItems={totalRecoleccion}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPageRecoleccion}
              />
            </TabsContent>

            {/* Tab Entregadas */}
            <TabsContent value="entregadas" className="space-y-4">
              <DataTable
                columns={columnsEntregadas}
                data={entregasEntregadas}
                isLoading={isLoadingEntregadas}
                onAction={handleAction}
              />
              <Pagination
                currentPage={currentPageEntregadas}
                totalItems={totalEntregadas}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPageEntregadas}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Modal de detalle */}
      <DetalleSolicitudModal
        isOpen={isDetalleModalOpen}
        onClose={() => {
          setIsDetalleModalOpen(false);
          setSelectedSolicitud(null);
        }}
        solicitud={selectedSolicitud}
        tipo="lenceria"
      />

      {/* Modal de alistamiento */}
      <AlistarLenceriaModal
        isOpen={isAlistarModalOpen}
        onClose={() => {
          setIsAlistarModalOpen(false);
          setSolicitudAlistar(null);
        }}
        solicitud={solicitudAlistar}
        onSuccess={handleAlistamientoSuccess}
      />

      {/* Modal de comprobante */}
      <VerComprobanteModal
        isOpen={isComprobanteModalOpen}
        onClose={() => {
          setIsComprobanteModalOpen(false);
          setSolicitudComprobante(null);
        }}
        solicitud={solicitudComprobante}
      />

      {/* Modal de entrega directa */}
      <CrearEntregaDirectaModal
        isOpen={isEntregaDirectaModalOpen}
        onClose={() => setIsEntregaDirectaModalOpen(false)}
        onSuccess={handleEntregaDirectaSuccess}
      />

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        fields={filterFields}
        initialFilters={filters}
        onApplyFilters={handleApplyFilters}
      />

      {/* Dialog de confirmación: eliminar alistamiento */}
      <AlertDialog
        open={!!entregaACancelar}
        onOpenChange={(open) => {
          if (!open && !isCancelingEntrega) {
            setEntregaACancelar(null);
            setMotivoCancelacion('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Eliminar alistamiento
            </AlertDialogTitle>
            <AlertDialogDescription>
              {entregaACancelar && (
                <>
                  Vas a eliminar el alistamiento{' '}
                  <strong className="text-foreground">
                    {entregaACancelar.consecutivo_entrega || entregaACancelar._id}
                  </strong>{' '}
                  de{' '}
                  <strong className="text-foreground">
                    {entregaACancelar.hospedaje?.nombre || entregaACancelar.nombrePropiedad || 'la entrega'}
                  </strong>
                  .
                  <br />
                  <br />
                  <span className="text-amber-700 bg-amber-50 px-2 py-1 rounded">
                    El inventario no se ve afectado: el alistamiento no descuenta stock.
                  </span>
                  <br />
                  <br />
                  Esta acción no se puede deshacer. Indica el motivo:
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor="motivo-cancelacion">Motivo *</Label>
            <Input
              id="motivo-cancelacion"
              value={motivoCancelacion}
              onChange={(e) => setMotivoCancelacion(e.target.value)}
              placeholder="Ej: Error de alistamiento, solicitud duplicada..."
              disabled={isCancelingEntrega}
              autoFocus
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelingEntrega}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarCancelacion}
              disabled={isCancelingEntrega || !motivoCancelacion.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isCancelingEntrega ? 'Eliminando...' : 'Eliminar alistamiento'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default RecepcionRopaPage;
